// ⌘K command-palette (a design terv cmdk-mintája): navigációs ugrások (jog-szűrten) + VALÓDI kereszt-entitás
// keresés (BFF `GET /search`). Radix Dialogra épül (fókusz-csapda/Escape). Két szekció: „Navigáció" (a
// nav-itemek `q`-ra szűrve) + „Találatok" (a keresés eredménye típusonként csoportosítva). A billentyű-nav egy
// KOMBINÁLT lapos listán megy át (nav + összes találat), a kijelölt elem a látható részbe görget.
import { GLOBAL_SEARCH_TYPES } from '@atlas/contracts';
import { ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { type ComponentType, type SVGProps, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { navGroups, navItemVisible } from '@/components/shared/nav-config';
import {
  SEARCH_TYPE_ICONS,
  SEARCH_TYPE_LABEL_KEYS,
  type SearchResultType,
  navigateToSearchResult,
} from '@/components/shared/search-targets';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/use-permissions';
import { cn } from '@/lib/utils';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

// A kereséshez a `q` debounce-olt tükre (nem minden leütésre megy hálózati hívás). 220 ms — a lista-oldalak
// szerver-kereső debounce-ával (300 ms) egy nagyságrend, de a palette-hez pörgősebb.
const SEARCH_DEBOUNCE_MS = 220;
const MIN_QUERY_LENGTH = 2;

// A kombinált (nav + találat) lista lapos eleme — a billentyű-nav ezen az egy indexen megy végig.
type FlatItem =
  | { kind: 'nav'; key: string; label: string; icon: IconComponent; to: string }
  | {
      kind: 'result';
      key: string;
      label: string;
      sublabel?: string | null;
      icon: IconComponent;
      type: SearchResultType;
      id: string;
    };

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // A `q` debounce-olt tükre → ez vezérli a keresést. A <2 karakter is átmegy, de a query `enabled`-je tiltja.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [q]);

  const searchEnabled = debouncedQ.length >= MIN_QUERY_LENGTH;
  // A SZEMÉLY-találatok névsorrendje a szerveren dől el a nyelv szerint (`personName`) → a nyelv a
  // query-KULCS része is, különben nyelvváltáskor a cache-elt (rossz sorrendű) találat maradna.
  const lang: 'hu' | 'en' = i18n.language === 'en' ? 'en' : 'hu';
  const searchQuery = api.globalSearch.useQuery(
    ['global-search', debouncedQ, lang],
    { query: { q: debouncedQ, lang } },
    { queryKey: ['global-search', debouncedQ, lang], enabled: searchEnabled, staleTime: 30_000 },
  );
  // Best-effort: a szerver hibája NE döntse meg a palette-et — hiba esetén egyszerűen nincs találat-szekció.
  const results = useMemo(
    () => (searchQuery.data?.status === 200 ? searchQuery.data.body.results : []),
    [searchQuery.data],
  );

  /**
   * A szerver KIMONDJA, ha a találat-lista nem teljes (lap-plafon vagy kimaradt típus). Ezt meg kell
   * mutatni: a néma, hiányos lista rosszabb, mint a bevallott részleges — a felhasználó különben azt hiszi,
   * hogy amit keres, nem is létezik. Csak akkor jelenítjük meg, ha tényleg keresés futott.
   */
  const partial = useMemo(
    () => searchQuery.data?.status === 200 && searchQuery.data.body.partial,
    [searchQuery.data],
  );

  // Navigációs szekció: a jog-szűrt nav-itemek `q`-ra szűrve (a `< 2` karakter esetén is ez fut, mint eddig).
  const navItems = useMemo<FlatItem[]>(() => {
    const all = navGroups
      .flatMap((g) => g.items)
      .filter((i) => i.to && navItemVisible(i, can))
      .map<FlatItem>((i) => ({
        kind: 'nav',
        key: `nav:${i.to!}`,
        to: i.to!,
        label: t(i.labelKey),
        icon: i.icon,
      }));
    const needle = q.trim().toLowerCase();
    return needle ? all.filter((i) => i.label.toLowerCase().includes(needle)) : all;
  }, [q, can, t]);

  // Találat-szekció: a lapos találatok TÍPUS szerint csoportosítva, a `GLOBAL_SEARCH_TYPES` sorrendjében.
  const resultGroups = useMemo(
    () =>
      GLOBAL_SEARCH_TYPES.map((type) => ({
        type,
        items: results.filter((r) => r.type === type),
      })).filter((g) => g.items.length > 0),
    [results],
  );

  const resultItems = useMemo<FlatItem[]>(
    () =>
      resultGroups.flatMap((g) =>
        g.items.map<FlatItem>((r) => ({
          kind: 'result',
          key: `result:${r.type}:${r.id}`,
          label: r.label,
          sublabel: r.sublabel ?? null,
          icon: SEARCH_TYPE_ICONS[r.type],
          type: r.type,
          id: r.id,
        })),
      ),
    [resultGroups],
  );

  // A KOMBINÁLT lapos lista (nav elöl, találatok a render-sorrendben) — a billentyű-nav ezen megy végig.
  const flat = useMemo(() => [...navItems, ...resultItems], [navItems, resultItems]);
  const indexByKey = useMemo(() => new Map(flat.map((it, i) => [it.key, i])), [flat]);

  useEffect(() => {
    if (open) {
      setQ('');
      setDebouncedQ('');
      setIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);
  useEffect(() => {
    setIdx(0);
  }, [q]);
  // Ha a kombinált lista ZSUGORODIK (pl. késleltetett találatok eltűnnek), az `idx` ne mutasson tartományon
  // kívülre — különben az Enter/kijelölés némán no-op lenne. Klampoljuk a `flat` hosszához.
  useEffect(() => {
    setIdx((i) => Math.min(i, Math.max(0, flat.length - 1)));
  }, [flat.length]);
  // A kijelölt elem GÖRDÜLJÖN a látható részbe (különben a nyilas navigáció „elhagyja" a scroll-ablakot,
  // és alul nem látszik, melyik soron állunk). `block: 'nearest'` — csak akkor görget, ha ténylegesen kell.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [idx]);

  const activate = (it: FlatItem | undefined): void => {
    if (!it) return;
    onOpenChange(false);
    if (it.kind === 'nav') {
      void navigate({ to: it.to });
    } else {
      navigateToSearchResult(navigate, it.type, it.id);
    }
  };

  const showSearching = searchEnabled && searchQuery.isFetching && resultItems.length === 0;
  const showEmpty = flat.length === 0 && !showSearching;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=open]:fade-in-0 app-fixed-fill fixed inset-0 z-50 bg-[rgba(10,13,24,0.45)] backdrop-blur-[6px]" />
        <DialogPrimitive.Content
          className="text-foreground data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2 fixed left-1/2 top-[12vh] z-50 w-[min(620px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-2xl border bg-[var(--app-surface-glass)]"
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setIdx((i) => Math.min(i + 1, flat.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setIdx((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              activate(flat[idx]);
            }
          }}
        >
          <DialogPrimitive.Title className="sr-only">{t('nav.search')}</DialogPrimitive.Title>
          {/* Fejléc — 14px input, karcsú (~44px) sor: a Linear/Raycast ⌘K-arányai */}
          <div className="flex items-center gap-2.5 px-3.5 py-2.5">
            <MagnifyingGlassIcon className="text-muted-foreground size-4 shrink-0" />
            {/* A globális `Input` — az `input-glass` fókusz-ring és a `placeholder:text-subtle` ONNAN jön.
                A palette-specifikus elrendezés override-olva: nincs keret (a dialógus-fejlécben zaj lenne),
                tartalom-magasság (a sor ~44px-e a wrapper `py-2.5`-éből jön), 16px szöveg. */}
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('nav.searchPlaceholder')}
              className="h-auto flex-1 rounded-md border-none px-2 text-base"
            />
            <kbd className="bg-sunken text-2xs text-muted-foreground rounded border px-1 py-px font-mono">
              ESC
            </kbd>
          </div>
          <div className="max-h-[400px] overflow-y-auto p-1.5">
            {showEmpty && (
              <div className="text-md text-muted-foreground px-3.5 py-9 text-center">
                {t('nav.noResults')}
              </div>
            )}

            {/* Navigáció szekció — a jog-szűrt nav-itemek `q`-ra szűrve. */}
            {navItems.length > 0 && (
              <div className="text-2xs text-muted-foreground px-2.5 pb-1 pt-1.5 font-semibold uppercase tracking-[0.07em]">
                {t('nav.navigation')}
              </div>
            )}
            {navItems.map((it) => {
              const i = indexByKey.get(it.key) ?? -1;
              return (
                <button
                  key={it.key}
                  ref={i === idx ? activeRef : undefined}
                  type="button"
                  onMouseEnter={() => setIdx(i)}
                  onClick={() => activate(it)}
                  className={cn(
                    'text-md flex w-full items-center gap-2.5 rounded-md px-2.5 py-[7px] text-left transition-colors',
                    // Kijelölés = a sidebar aktív-mintája (semleges tónus + zöld ikon) — brand háttérként nem használt
                    i === idx ? 'bg-active text-foreground' : 'hover:bg-accent',
                  )}
                >
                  <it.icon
                    className={cn('size-3.5', i === idx ? 'text-brand' : 'text-muted-foreground')}
                  />
                  <span>{it.label}</span>
                </button>
              );
            })}

            {/* Keresés folyamatban — finom jelző (csak amíg nincs mit mutatni). */}
            {showSearching && (
              <div className="text-md text-muted-foreground px-3.5 py-6 text-center">
                {t('nav.searching')}
              </div>
            )}

            {/* Találatok szekció — típusonként csoportosítva (a menü-sorrendben). */}
            {resultGroups.map((group) => (
              <div key={group.type}>
                <div className="text-2xs text-muted-foreground px-2.5 pb-1 pt-2.5 font-semibold uppercase tracking-[0.07em]">
                  {t(SEARCH_TYPE_LABEL_KEYS[group.type])}
                </div>
                {group.items.map((r) => {
                  const key = `result:${r.type}:${r.id}`;
                  const i = indexByKey.get(key) ?? -1;
                  const Icon = SEARCH_TYPE_ICONS[r.type];
                  return (
                    <button
                      key={key}
                      ref={i === idx ? activeRef : undefined}
                      type="button"
                      onMouseEnter={() => setIdx(i)}
                      onClick={() => activate(flat[i])}
                      className={cn(
                        'text-md flex w-full items-center gap-2.5 rounded-md px-2.5 py-[7px] text-left transition-colors',
                        i === idx ? 'bg-active text-foreground' : 'hover:bg-accent',
                      )}
                    >
                      <Icon
                        className={cn(
                          'size-3.5 shrink-0',
                          i === idx ? 'text-brand' : 'text-muted-foreground',
                        )}
                      />
                      <span className="truncate">{r.label}</span>
                      {r.sublabel && (
                        <span className="text-subtle ml-auto truncate pl-3 text-xs">
                          {r.sublabel}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
            {/* RÉSZLEGES eredmény — a lista VÉGÉN, mert ez a lista egészére vonatkozó megjegyzés, nem egy
                találat tulajdonsága. Nem hiba-állapot (a találatok érvényesek), ezért nem `destructive`. */}
            {partial && searchEnabled && (
              <p className="text-subtle flex items-start gap-1.5 px-2.5 pb-1 pt-2.5 text-xs">
                <ExclamationTriangleIcon className="mt-px size-3.5 shrink-0" aria-hidden />
                {t('nav.partialResults')}
              </p>
            )}
          </div>
          {/* Lábléc — kbd-chipek + levegős térköz (a fejléc ESC-chipjével azonos stílus) */}
          <div className="text-muted-foreground flex items-center justify-between px-3.5 py-2.5 text-xs">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <kbd className="bg-sunken text-2xs rounded border px-1 py-px font-mono">↑↓</kbd>
                {t('nav.navigate')}
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="bg-sunken text-2xs rounded border px-1 py-px font-mono">↵</kbd>
                {t('nav.open')}
              </span>
            </div>
            <span className="text-subtle">Atlas</span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
